import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPostRow {
  id: string;
  slug: string;
  brand_key: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  keywords: string | null;
  audience: string | null;
  hero_icon: string;
  sections: { heading: string; content: string }[];
  cta: { heading: string; text: string; buttonText: string; buttonLink: string } | null;
  excerpt: string | null;
  state_code: string | null;
  city_name: string | null;
  content_type: string;
  ai_generated: boolean;
  status: string;
  publish_date: string | null;
  read_time: string | null;
  view_count: number;
  created_at: string;
  updated_at: string;
  approved_by: string | null;
  approved_at: string | null;
  denial_reason: string | null;
  og_image_url: string | null;
  external_url: string | null;
  ai_model: string | null;
  ai_prompt_summary: string | null;
  generation_run_id: string | null;
  canonical_url: string | null;
}

/** Fetch published blog posts, optionally filtered by state and/or brand. */
export function useBlogPosts(opts?: {
  stateCode?: string;
  brandKey?: string;
  limit?: number;
  status?: string;
}) {
  const { stateCode, brandKey, limit, status = "published" } = opts ?? {};

  return useQuery<BlogPostRow[]>({
    queryKey: ["blog_posts", { stateCode, brandKey, limit, status }],
    queryFn: async () => {
      let q = supabase
        .from("blog_posts" as any)
        .select("*")
        .eq("status", status)
        .order("publish_date", { ascending: false });

      if (brandKey) q = q.eq("brand_key", brandKey);
      if (stateCode) q = q.eq("state_code", stateCode);
      if (limit) q = q.limit(limit);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as BlogPostRow[];
    },
  });
}

/** Fetch a single blog post by slug. */
export function useBlogPost(slug: string | undefined) {
  return useQuery<BlogPostRow | null>({
    queryKey: ["blog_post", slug],
    enabled: !!slug,
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as BlogPostRow | null;
    },
  });
}

/** Fetch all blog posts for admin (any status). */
export function useAdminBlogPosts(opts?: {
  status?: string;
  contentType?: string;
  stateCode?: string;
  brandKey?: string;
}) {
  const { status, contentType, stateCode, brandKey } = opts ?? {};

  return useQuery<BlogPostRow[]>({
    queryKey: ["admin_blog_posts", { status, contentType, stateCode, brandKey }],
    queryFn: async () => {
      let q = supabase
        .from("blog_posts" as any)
        .select("*")
        .order("created_at", { ascending: false });

      if (status) q = q.eq("status", status);
      if (contentType) q = q.eq("content_type", contentType);
      if (stateCode) q = q.eq("state_code", stateCode);
      if (brandKey) q = q.eq("brand_key", brandKey);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as BlogPostRow[];
    },
  });
}

/** Fetch pending items for the unified approval queue (blog + social). */
export function usePendingApprovalQueue() {
  return useQuery({
    queryKey: ["pending_approval_queue"],
    refetchInterval: 30_000,
    queryFn: async () => {
      // Fetch pending blog posts
      const { data: blogData, error: blogErr } = await supabase
        .from("blog_posts" as any)
        .select("id, slug, title, state_code, content_type, excerpt, created_at, ai_generated, brand_key, sections")
        .eq("status", "pending_approval")
        .order("created_at", { ascending: true });

      if (blogErr) throw blogErr;

      // Fetch pending social posts
      const { data: socialData, error: socialErr } = await supabase
        .from("social_posts" as any)
        .select("id, platform, content, state_code, scheduled_at, created_at, ai_generated, blog_post_id, content_type")
        .eq("status", "pending_approval")
        .order("created_at", { ascending: true });

      if (socialErr) throw socialErr;

      const blogItems = ((blogData ?? []) as any[]).map((b: any) => ({
        id: b.id,
        type: "blog" as const,
        title: b.title,
        stateCode: b.state_code,
        contentType: b.content_type,
        preview: b.excerpt || b.title,
        sections: b.sections,
        createdAt: b.created_at,
        aiGenerated: b.ai_generated,
        brandKey: b.brand_key,
        slug: b.slug,
      }));

      const socialItems = ((socialData ?? []) as any[]).map((s: any) => ({
        id: s.id,
        type: "social" as const,
        title: s.content?.substring(0, 80) || "Social post",
        stateCode: s.state_code,
        contentType: s.content_type || "standalone",
        preview: s.content || "",
        platform: s.platform,
        blogPostId: s.blog_post_id,
        createdAt: s.created_at,
        aiGenerated: s.ai_generated,
      }));

      // Merge and sort by created_at
      return [...blogItems, ...socialItems].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    },
  });
}
